import { __ } from '@wordpress/i18n';
import { useEffect, useState } from 'react';
import {CustomSelectControl, FontSizePicker, SelectControl,
  __experimentalToolsPanel as ToolsPanel,
  __experimentalToolsPanelItem as ToolsPanelItem,
  __experimentalUnitControl as UnitControl,
  ComboboxControl
} from '@wordpress/components';

import { LineHeightControl,
    __experimentalWritingModeControl   as WritingModeControl,
    __experimentalTextDecorationControl as TextDecorationControl,
    __experimentalTextTransformControl  as TextTransformControl,
    __experimentalFontFamilyControl  as FontFamilyControl
 } from '@wordpress/block-editor';
 import googleFontsLoader from '../../helpers/google-fonts.js';

import { defaultAppearance, defaultUnits,defaultFontSize} from './default-data';
import { VayuBlocksDevice,VayuBlocksDeviceControl } from '@vayu-blocks/device-control';
import { VayuBlocksBlockEditor } from '@vayu-blocks/global';

export function VayuBlocksTypography({
  label = 'Typography',
  onChange,
  value = {
    fonts: {},
    fontSize:  {},
    appearance:  {},
    lineHeights:  {},
    letterSpacing:  {},
    decoration:  {},
    orientation:  {},
    letterCase:  {},
  },
  defaultopen=[
    'fontSize'
  ],
}) {

    const getView = VayuBlocksDevice(); 
    const blockEditor = VayuBlocksBlockEditor();
    const [firstTablet, setFirstTablet] = useState(false);
    const [firstDesktop, setFirstDesktop] = useState(true);

    const resetAll = () => {
      const resetValues = Object.keys(value).reduce((acc, key) => {
        acc[key] = { Desktop: '', Tablet: '', Mobile: '' }; // Reset to empty values for all devices
        return acc;
      }, {});
      onChange(resetValues); // Pass the updated object to onChange
    };
  
    const handleChange = (key, newVal) => {

      const attr = value.key;

      const updatedAttributes = {
        ...value,
        [key]: {
            ...value[key],
            [getView]: newVal,
        },
      };

      const isFirstRun = attr?.[getView] === undefined;
      const isTabletFirstRun = localStorage.getItem('firstTablet') || firstTablet;

      if (getView === 'Desktop') {
        if (isFirstRun || firstDesktop) {
            Object.assign(updatedAttributes[key], { Tablet: newVal, Mobile: newVal });
            localStorage.setItem('firstTablet', 'true');
            setFirstTablet(true);
            setFirstDesktop(true);
        }
      } else if (getView === 'Tablet') {
          if (isTabletFirstRun) {
              Object.assign(updatedAttributes[key], { Mobile: newVal});
              localStorage.removeItem('firstTablet');
              setFirstTablet(true);
              setFirstDesktop(false);
          }
      } else {
          // For other device views, reset tablet/desktop flags
          setFirstTablet(false);
          setFirstDesktop(false);
      }
      onChange(updatedAttributes);
    };

    const getToolsPanelClass = (key, index, arr) => {
      if (key === 'fonts' || key === 'fontSize') return 'first';
      else if (index === arr.length - 1) return 'last';
      else return 'single-column';
    };


    const deviceLabel = <VayuBlocksDeviceControl label={label} />;
    // const allFontGroups = blockEditor.__experimentalFeatures?.typography?.fontFamilies || {};
    // const allFonts = Object.values(allFontGroups).flat();

    const [fonts, setFonts] = useState(null);
    
    useEffect(() => {
      googleFontsLoader.afterLoading().then((loader) => {
        const googleFonts = loader.fonts;
    
        // Group by first letter
        const grouped = googleFonts.reduce((acc, font) => {
          const firstLetter = font.family.charAt(0).toUpperCase();
          if (!acc[firstLetter]) {
            acc[firstLetter] = [];
          }
          acc[firstLetter].push(font);
          return acc;
        }, {});

        // Select few from each letter
        const limitedFonts = Object.keys(grouped)
          .flatMap((letter) => grouped[letter]); 
    
        //width slice
        // // Select few from each letter
        // const selectedFonts = Object.keys(grouped)
        //   .sort()
        //   .flatMap((letter) => grouped[letter].slice(0, 20)); // 20 from each A-Z (20 * 26 = ~520)
    
        // // Limit to 500 in case overflow
        // const limitedFonts = selectedFonts.slice(0, 500);
    
        setFonts(limitedFonts);
      });
    }, []);
  
    const allFontSizeGroups = blockEditor.__experimentalFeatures?.typography?.fontSizes || {};

  const newClampFontSizes = [
    {
      name: "Font Size 2em",
      slug: "font-size-2em",
      size: "clamp(10px, 1vw, 32px)"
    },
    {
      name: "Font Size 5em",
      slug: "font-size-5em",
      size: "clamp(14px, 1.8vw, 80px)"
    },
    {
      name: "Font Size 8em",
      slug: "font-size-8em",
      size: "clamp(18px, 2.5vw, 128px)"
    },
    {
      name: "Font Size 10em",
      slug: "font-size-10em",
      size: "clamp(22px, 3vw, 160px)"
    },
    {
      name: "Font Size 12em",
      slug: "font-size-12em",
      size: "clamp(26px, 3.8vw, 192px)"
    },
    {
      name: "Font Size 15em",
      slug: "font-size-15em",
      size: "clamp(30px, 4.2vw, 240px)"
    },
    {
      name: "Font Size 18em",
      slug: "font-size-18em",
      size: "clamp(34px, 4.8vw, 288px)"
    },
    {
      name: "Font Size 20em",
      slug: "font-size-20em",
      size: "clamp(36px, 5.2vw, 320px)"
    }
  ];

  const allFontSizes = [...Object.values(allFontSizeGroups).flat(), ...newClampFontSizes];

    return (
  
      <ToolsPanel
        label={deviceLabel}
        resetAll={resetAll}
        className='vayu-blocks-typography vayu-blocks-spacing-panel vayu-blocks-typography-toolspanel'
      >

        {Object.entries(value).map(([key, val],index,arr) => (
          <ToolsPanelItem
            key={key}
            hasValue={() => !!val[getView]}
            reset={resetAll}
            label={__(key.charAt(0).toUpperCase() + key.slice(1), 'vayu-blocks')}
            {...(defaultopen.includes(key) ? { isShownByDefault: true } : {})}
            className={`vayu-blocks-typography-toolspanel-item ${getToolsPanelClass(key, index, arr)}`}
          >


          {(fonts && key === 'fonts') && (
            <ComboboxControl
              label={__('Fonts', 'vayu-blocks')}
              value={value.fonts[getView] || ''}
              options={[
                {
                  label: __('Default', 'vayu-blocks'),
                  value: ''
                },
                ...fonts.map((font) => ({
                  label: font.family,
                  value: font.family
                }))
              ]}
              onChange={(value) => handleChange(key, value)}
            />
          )}

            {/* {key === 'fonts' && (
              <FontFamilyControl
                label={__('Fonts', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value)}
                value={value.fonts[getView]}
                fontFamilies={allFonts || defaultFonts}
              />
            )} */}

            {key === 'fontSize' && (
              <FontSizePicker
                label={__('Font Size', 'vayu_blocks')}
                fontSizes={allFontSizes || defaultFontSize}
                onChange={(value) => handleChange(key, value)}
                value={val[getView]}
                units={['px', 'em', 'rem']}
                withSlider
              />
            )}

            {key === 'appearance' && (
              <CustomSelectControl
                __next40pxDefaultSize={true}
                label={__('Appearance', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value.selectedItem)}
                options={defaultAppearance}
                value={val[getView]}
                className="vayu-blocks-typography-toolspanel-item-appearance"
              />
            )}

            {key === 'lineHeights' && (
              <LineHeightControl
                __next40pxDefaultSize={true}
                __unstableInputWidth="100px"
                onChange={(value) => handleChange(key, value)}
                value={val[getView]}
              />
            )}

            {key === 'letterSpacing' && (
              <UnitControl
                label={__('Letter Spacing', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value)}
                value={val[getView]}
                units={defaultUnits}
              />
            )}

            {key === 'decoration' && (
              <TextDecorationControl
                label={__('Decoration', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value)}
                value={val[getView]} 
              />
            )}

            {key === 'orientation' && (
              <WritingModeControl
                label={__('Orientation', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value)}
                value={val[getView]} 
              />
            )}

            {key === 'letterCase' && (
              <TextTransformControl
                label={__('Letter Case', 'vayu_blocks')}
                onChange={(value) => handleChange(key, value)}
                value={val[getView]}
              />
            )}

          </ToolsPanelItem>
        ))}

      </ToolsPanel>

  );
}